/**
 * @fileoverview Google Analytics Enhanced Ecommerce product impression tracking
 *
 * Tracks which product cards enter the viewport on collection and search pages,
 * pushing impression batches to the GA data layer in groups of 10.
 *
 * Each product-card dispatches `product-card:initialized` (bubbles) once its
 * product and variant data are ready. We combine that with IntersectionObserver
 * to record an impression only when both conditions are true:
 * the card is initialised AND has been at least 50% visible.
 *
 * Grid re-renders (filter/page changes) replace [data-products-container]
 * children entirely, so WeakMap-scoped reported sets naturally reset per
 * container without any explicit cleanup.
 */

const pageType = window.theme?.request?.page_type;

/** @type {string} */
const listType = pageType === 'search'
  ? 'Search results'
  : pageType === 'collection'
    ? 'Collection page'
    : '';

// Only run on list pages where impressions are meaningful.
if (listType) {

/** @type {string} */
const storeCurrencyCode = window.theme?.currency?.code;

// ─── Impression batch management ─────────────────────────────────────────

/** @type {Object[]} */
let pendingImpressions = [];

/** @type {number} */
let pushNumber = 1;

function buildImpressionPayload(impressions) {
  return {
    event: 'eec.impressionView',
    pushNumber: pushNumber++,
    ecommerce: { currencyCode: storeCurrencyCode, impressions },
  };
}

function queueImpression(impression) {
  pendingImpressions.push(impression);
  if (pendingImpressions.length >= 10) flushImpressions();
}

function flushImpressions() {
  if (!pendingImpressions.length) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(buildImpressionPayload(pendingImpressions));
  pendingImpressions = [];
}

// ─── Per-container reported-card tracking ────────────────────────────────
//
// Scoped to [data-products-container] via WeakMap so stale containers GC
// cleanly and re-renders of the same container start fresh automatically.

/** @type {WeakMap<HTMLElement, Set<HTMLElement>>} */
const reportedByContainer = new WeakMap();

function getReportedSet(container) {
  if (!reportedByContainer.has(container)) {
    reportedByContainer.set(container, new Set());
  }
  return reportedByContainer.get(container);
}

// ─── Two-condition gate ───────────────────────────────────────────────────
//
// A card is eligible when BOTH are true:
//   1. It has been at least 50% visible (intersectionObserver sets a flag)
//   2. product-card:initialized has fired (its data is ready)
//
// Whichever condition is met second calls tryRecord().

/**
 * Attempt to record an impression for a card if both conditions are met.
 * @param {HTMLElement} card
 */
function tryRecord(card) {
  if (!card._dlVisible || !card._dlReady) return;

  const container = card.closest('[data-products-container]');
  if (!container) return;

  const reported = getReportedSet(container);
  if (reported.has(card)) return;

  // Data was stashed from the event detail at init time — no getter calls needed.
  const { product, variant } = card._dlData;
  if (!product?.id || !variant?.id) return;

  const scope = card.closest('product-grid') ?? document;
  const position = Array.from(scope.querySelectorAll('product-card')).indexOf(card) + 1;

  reported.add(card);
  intersectionObserver.unobserve(card);

  queueImpression({
    name: product.title,
    id: product.id,
    price: parseFloat(((variant.price ?? product.price ?? 0) / 100).toFixed(2)),
    brand: product.vendor,
    category: product.type,
    variant: variant.id,
    list: listType,
    position,
  });
}

// ─── IntersectionObserver ────────────────────────────────────────────────

const intersectionObserver = new IntersectionObserver(entries => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const card = entry.target;
    card._dlVisible = true;
    tryRecord(card);
  }
}, { threshold: 0.5 });

// ─── product-card:initialized listener ───────────────────────────────────

// Bubbles from individual cards up to document — one listener handles all.
// Stash detail data on the card so tryRecord() doesn't need to call getters.
document.addEventListener('product-card:initialized', event => {
  const card = event.target;
  card._dlData = event.detail;
  card._dlReady = true;
  tryRecord(card);
});

// ─── Start observing cards ────────────────────────────────────────────────

function observeCard(card) {
  intersectionObserver.observe(card);
}

// MutationObserver picks up cards injected by ProductGrid renders.
const mutationObserver = new MutationObserver(mutations => {
  for (const mutation of mutations) {
    if (mutation.type !== 'childList') continue;
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      const cards = node.tagName?.toLowerCase() === 'product-card'
        ? [node]
        : Array.from(node.querySelectorAll?.('product-card') || []);
      for (const card of cards) observeCard(card);
    }
  }
});

window.addEventListener('pagehide', flushImpressions);
window.addEventListener('beforeunload', flushImpressions);

function init() {
  for (const card of document.querySelectorAll('product-card')) observeCard(card);
  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

init();

} // end if (listType)
